/*
  Global scroll reveal, parallax, and sticky CTA system.
  Works alongside existing section-level JS.
*/
(function () {
  'use strict';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ──────────────────────────────────────────────────────────
     1. SCROLL REVEAL  —  [data-reveal] driven
     ────────────────────────────────────────────────────────── */
  function initReveal() {
    if (prefersReducedMotion()) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseFloat(el.dataset.revealDelay || 0) * 1000;
          setTimeout(() => el.classList.add('is-revealed'), delay);
          revealObs.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('[data-reveal]').forEach((el) => revealObs.observe(el));
  }

  /* ──────────────────────────────────────────────────────────
     2. STAGGER CHILDREN  —  [data-reveal-stagger]
     ────────────────────────────────────────────────────────── */
  function initStagger() {
    if (prefersReducedMotion()) {
      document.querySelectorAll('[data-reveal-stagger]').forEach((parent) => {
        parent.classList.add('is-revealed');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal-stagger]').forEach((parent) => {
        parent.classList.add('is-revealed');
      });
      return;
    }

    const staggerObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const parent = entry.target;
          const children = Array.from(parent.children);
          children.forEach((child, i) => {
            child.style.setProperty('--stagger-i', i);
          });
          parent.classList.add('is-revealed');
          staggerObs.unobserve(parent);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    document.querySelectorAll('[data-reveal-stagger]').forEach((el) => staggerObs.observe(el));
  }

  /* ──────────────────────────────────────────────────────────
     3. PARALLAX  —  [data-parallax] attribute
        Uses rAF, desktop only (>= 992px)
     ────────────────────────────────────────────────────────── */
  function initParallax() {
    if (prefersReducedMotion()) return;
    if (window.innerWidth < 992) return;

    const parallaxEls = document.querySelectorAll('[data-parallax]');
    if (!parallaxEls.length) return;

    let ticking = false;

    function update() {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;

      parallaxEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        // Only apply when element is in or near viewport
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const offset = (rect.top + scrollY - vh / 2) * speed;
        el.style.transform = `translate3d(0, ${-offset}px, 0)`;
      });

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update(); // initial
  }

  /* ──────────────────────────────────────────────────────────
     4. STICKY CTA BAR  —  product detail page
     ────────────────────────────────────────────────────────── */
  function initStickyCTA() {
    const stickyBar = document.querySelector('.sticky-cta-bar');
    if (!stickyBar) return;

    // Find the primary ATC button (name="add")
    const atcBtn = document.querySelector('button[name="add"], [name="add"]');
    if (!atcBtn) return;

    const stickyObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '80px 0px 0px 0px' }
    );

    stickyObs.observe(atcBtn);
  }

  /* ──────────────────────────────────────────────────────────
     5. COUNTER ANIMATION  —  [data-count-to]
        Animates numeric value from 0 to target on scroll.
     ────────────────────────────────────────────────────────── */
  function initCounters() {
    if (prefersReducedMotion()) return;
    if (!('IntersectionObserver' in window)) return;

    const counterObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.countTo);
          if (isNaN(target)) return;

          const suffix = el.dataset.countSuffix || '';
          const prefix = el.dataset.countPrefix || '';
          const decimals = parseInt(el.dataset.countDecimals || 0);
          const duration = parseInt(el.dataset.countDuration || 1800);
          const startTime = performance.now();

          function step(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = target * eased;

            const formatted = decimals > 0
              ? current.toFixed(decimals)
              : Math.round(current).toLocaleString();

            el.textContent = prefix + formatted + suffix;

            if (progress < 1) {
              requestAnimationFrame(step);
            } else {
              el.textContent = prefix + (decimals > 0 ? target.toFixed(decimals) : target.toLocaleString()) + suffix;
            }
          }

          requestAnimationFrame(step);
          counterObs.unobserve(el);
        });
      },
      { threshold: 0.4 }
    );

    document.querySelectorAll('[data-count-to]').forEach((el) => counterObs.observe(el));
  }

  /* ──────────────────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────────────────── */
  /* ──────────────────────────────────────────────────────────
     6. ANCHOR SMOOTH SCROLL  —  intercept nav links with #section-*
     ────────────────────────────────────────────────────────── */
  function initAnchorScroll() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="#section-"]');
      if (!link) return;
      const hash = link.getAttribute('href').split('#')[1];
      if (!hash) return;
      const target = document.getElementById(hash);
      if (!target) return;
      e.preventDefault();
      const headerH = document.querySelector('.header-wrapper')?.offsetHeight || 80;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerH;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  function init() {
    initReveal();
    initStagger();
    initParallax();
    initStickyCTA();
    initCounters();
    initAnchorScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, 50);
  });
})();
